// Every color is a CSS variable with the original light-mode value baked in as
// the var() fallback. That means anything importing T keeps rendering exactly
// as before unless it happens to sit under a DOM node that defines these
// variables — which only Dashboard.js's dark-mode wrapper does. So this is
// safe to import from any other page without side effects.
export const T = {
  bg: "var(--k-bg, #FFFFFF)", surface: "var(--k-surface, #FFFFFF)", surface2: "var(--k-surface2, #F7F6FF)", surface3: "var(--k-surface3, #F0EFFE)",
  border: "var(--k-border, #E4E0F8)", borderHover: "var(--k-borderHover, #C9C2F0)",
  text: "var(--k-text, #1A1D2B)", textMuted: "var(--k-textMuted, #5E6278)", textDim: "var(--k-textDim, #8E92A6)",
  accent: "var(--k-accent, #7c3aed)", accentSoft: "var(--k-accentSoft, rgba(124,58,237,0.08))", accentHover: "var(--k-accentHover, #6d28d9)",
  accentGlow: "var(--k-accentGlow, 0 0 12px rgba(124,58,237,0.25), 0 0 32px rgba(124,58,237,0.1))",
  green: "var(--k-green, #059669)", greenSoft: "var(--k-greenSoft, rgba(5,150,105,0.10))",
  orange: "var(--k-orange, #D48A00)", orangeSoft: "var(--k-orangeSoft, rgba(212,138,0,0.10))",
  red: "var(--k-red, #DC4545)", redSoft: "var(--k-redSoft, rgba(220,69,69,0.10))",
  blue: "var(--k-blue, #2B7FD4)", blueSoft: "var(--k-blueSoft, rgba(43,127,212,0.10))",
  font: "'DM Sans', sans-serif", mono: "'JetBrains Mono', monospace",
  radius: 10, radiusSm: 6, shadow: "var(--k-shadow, 0 4px 24px rgba(124,58,237,0.08))",
};

// Dark-mode overrides for the variables above. Applied only on the element
// carrying data-karoline-theme="dark" (Dashboard.js's page wrapper), so it
// cascades to every descendant without touching any other page.
export const DARK_THEME_CSS = `
[data-karoline-theme="dark"] {
  --k-bg: #14121f;
  --k-surface: #1b1930;
  --k-surface2: #241f3d;
  --k-surface3: #2c2650;
  --k-border: #352f57;
  --k-borderHover: #453d70;
  --k-text: #EDEBFA;
  --k-textMuted: #A9A6C4;
  --k-textDim: #7B7796;
  --k-accent: #9b6bf2;
  --k-accentSoft: rgba(155,107,242,0.16);
  --k-accentHover: #8354e8;
  --k-accentGlow: 0 0 12px rgba(155,107,242,0.35), 0 0 32px rgba(155,107,242,0.15);
  --k-green: #34d399;
  --k-greenSoft: rgba(52,211,153,0.14);
  --k-orange: #f0a93a;
  --k-orangeSoft: rgba(240,169,58,0.14);
  --k-red: #f16565;
  --k-redSoft: rgba(241,101,101,0.14);
  --k-blue: #5b9ee6;
  --k-blueSoft: rgba(91,158,230,0.14);
  --k-shadow: 0 4px 24px rgba(0,0,0,0.45);
}
`;

export const badgeColors = {
  green: { bg: T.greenSoft, color: T.green },
  orange: { bg: T.orangeSoft, color: T.orange },
  red: { bg: T.redSoft, color: T.red },
  blue: { bg: T.blueSoft, color: T.blue },
  dim: { bg: "rgba(156,160,176,0.1)", color: T.textMuted },
};

export const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return d;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export const rate = (a, t) => (t === 0 ? 0 : (a / t) * 100);
export const fmtRate = (a, t) => rate(a, t).toFixed(1) + "%";
export const rateBadge = (pct) => (pct >= 80 ? "green" : pct >= 60 ? "orange" : "red");

export const fieldInput = {
  width: "100%", background: T.surface2, border: `1px solid ${T.border}`,
  borderRadius: T.radiusSm, color: T.text, fontFamily: T.font,
  fontSize: 14, padding: "9px 12px", outline: "none",
};

export const S = {
  layout: { display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: T.font, color: T.text, background: T.bg },
  topnav: { background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "0 28px", display: "flex", alignItems: "center", height: 52, flexShrink: 0, boxShadow: "0 1px 0 rgba(124,58,237,0.08)" },
  brand: { fontSize: 13, fontWeight: 700, color: T.accent, letterSpacing: 4, textTransform: "uppercase", marginRight: 28, fontFamily: "'Orbitron', 'DM Sans', sans-serif", textShadow: "0 0 10px rgba(124,58,237,0.5), 0 0 24px rgba(124,58,237,0.2)" },
  navItem: (active) => ({
    display: "flex", alignItems: "center", gap: 7, padding: "6px 14px", height: 36, margin: "0 2px",
    color: active ? "#7c3aed" : T.textMuted, fontSize: 14, fontWeight: 500, cursor: "pointer",
    borderRadius: 8, transition: "all 0.15s", whiteSpace: "nowrap",
    background: active ? "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(232,146,10,0.08))" : "none",
    border: active ? "1px solid rgba(124,58,237,0.25)" : "1px solid transparent",
    boxShadow: active ? "0 0 12px rgba(124,58,237,0.18), 0 0 20px rgba(232,146,10,0.07)" : "none",
  }),
  content: { flex: 1, padding: 28, overflowY: "auto" },
  // Inner wrapper that actually caps line length — content itself stays
  // flex:1/overflowY:auto so scrolling still spans the full width (avoids a
  // dead-space scrollbar gutter), while this centers the cards themselves.
  contentInner: { maxWidth: 1200, margin: "0 auto" },
  subtabs: { display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, margin: "-28px -28px 24px", padding: "0 28px", background: T.surface },
  subtab: (active) => ({
    padding: "12px 20px", fontSize: 14, fontWeight: 500, color: active ? T.accent : T.textMuted,
    cursor: "pointer", borderBottom: `2px solid ${active ? T.accent : "transparent"}`,
    transition: "all 0.15s", position: "relative", bottom: -1, background: "none", border: "none",
    borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: active ? T.accent : "transparent",
  }),
  innertab: (active) => ({
    padding: "9px 16px", fontSize: 13, fontWeight: 500, color: active ? T.accent : T.textDim,
    cursor: "pointer", transition: "all 0.15s", position: "relative", bottom: -1,
    background: active ? "linear-gradient(135deg, rgba(124,58,237,0.08), rgba(232,146,10,0.05))" : "none",
    border: "none", borderBottom: `2px solid ${active ? T.accent : "transparent"}`,
    borderBottomWidth: 2, borderBottomStyle: "solid", borderBottomColor: active ? T.accent : "transparent",
  }),
  tableWrap: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: "0 2px 12px rgba(124,58,237,0.06)" },
  th: { textAlign: "left", padding: "10px 16px", fontSize: 12, fontWeight: 600, color: T.textDim, textTransform: "uppercase", letterSpacing: 0.8, background: T.surface2, borderBottom: `1px solid ${T.border}` },
  td: { padding: "12px 16px", fontSize: 14, color: T.text, borderBottom: `1px solid ${T.border}` },
  mono: { fontFamily: T.mono, fontSize: 13 },
  muted: { color: T.textMuted },
  filterSelect: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, color: T.text, fontFamily: T.font, fontSize: 14, padding: "8px 32px 8px 12px", cursor: "pointer", outline: "none" },
  filterInput: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, color: T.text, fontFamily: T.font, fontSize: 14, padding: "8px 12px 8px 32px", outline: "none", width: 220, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239CA0B0' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "8px center", backgroundSize: "14px" },
};
